/**
 * Per Medewerker Matrix (Epic C-2 — Kwalificaties, Story C-2.2, UX-C3)
 *
 * Links: medewerker-lijst (zoekveld + afdeling-filter). Rechts: één sectie per
 * activiteit met rijen=productcategorieën.
 *
 * Alleen de activiteiten die een doelmarkt kénnen (vastleggen en valideren
 * vastleggen) krijgen kolommen per doelmarkt. Bij (her)controle bestaat die as
 * niet — daar staat één kolom met een enkel vinkje per categorie.
 */
import { useMemo, useState } from 'react';
import {
  PLANNING_TASK_ACTIVITIES,
  PLANNING_TASK_ACTIVITY_LABELS,
  activityUsesTargetMarket,
  targetMarketKey,
} from '@functieloonhuis/shared';
import { useQualificationListItems } from '../hooks/useQualificationLists';
import { useEmployeeQualifications } from '../hooks/useQualifications';
import { QualificationCheckbox } from './QualificationCheckbox';
import type { PlanningTaskActivity, QualificationScopedEmployeeDTO } from '@functieloonhuis/shared';

interface PerMedewerkerMatrixProps {
  /** Al op afdeling gefilterd door de pagina; hier alleen nog de zoekterm. */
  employees: QualificationScopedEmployeeDTO[];
  isLoading: boolean;
}

export function PerMedewerkerMatrix({ employees, isLoading }: PerMedewerkerMatrixProps) {
  const [search, setSearch] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const { data: productCategories } = useQualificationListItems('product-categories');
  const { data: targetMarkets } = useQualificationListItems('target-markets');
  const { data: employeeQualifications } = useEmployeeQualifications(selectedEmployeeId);

  // Zoeken gebeurt hier, niet op de server: de lijst is al binnen en klein, en
  // een server-zoekopdracht zou de afdelingsopties op de pagina laten meekrimpen
  // — dan vindt zoeken buiten je eigen afdeling stilzwijgend niemand.
  const needle = search.trim().toLowerCase();
  const employeeList = needle
    ? employees.filter((emp) => emp.name.toLowerCase().includes(needle))
    : employees;

  const selectedEmployee = employeeList.find((e) => e.id === selectedEmployeeId) ?? null;
  // `targetMarketKey` aan BEIDE kanten: `${null}` is niet `${0}`, dus zonder
  // normalisatie staat elk markt-loos vakje leeg terwijl de kwalificatie wél
  // bestaat — en het aanvinken maakt dan een tweede, geweigerde grant.
  const qualifiedKeys = useMemo(
    () =>
      new Set(
        (employeeQualifications ?? []).map(
          (q) => `${q.activity}:${q.productCategoryId}:${targetMarketKey(q.targetMarketId)}`
        )
      ),
    [employeeQualifications]
  );

  const categories = productCategories ?? [];
  const markets = targetMarkets ?? [];
  // Alleen de categorieënlijst is voor élke sectie nodig. Zou hier ook een
  // doelmarkt geëist worden, dan zou een lege doelmarktenlijst ook de vier
  // markt-loze secties wissen — terwijl die er niets mee te maken hebben.
  const hasLists = categories.length > 0;

  /** De kolom-as van één sectie: de doelmarkten, of één naamloze kolom. */
  const marketAxis = (
    activity: PlanningTaskActivity
  ): { id: number | null; name: string | null }[] =>
    activityUsesTargetMarket(activity) ? markets : [{ id: null, name: null }];

  return (
    <div className="flex gap-6">
      <div className="w-64 flex-shrink-0">
        <input
          type="text"
          data-testid="medewerker-zoek-input"
          placeholder="Zoek medewerker…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-2 w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#54949E]"
        />
        {isLoading && (
          <p data-testid="medewerker-lijst-laden" className="py-4 text-sm text-gray-500">
            Bezig met laden…
          </p>
        )}
        {!isLoading && employeeList.length === 0 && (
          // Anders staat er een lege kolom zonder uitleg, wat leest als "er is
          // niemand" terwijl het net zo goed de zoekterm kan zijn.
          <p data-testid="medewerker-lijst-leeg" className="py-4 text-sm text-gray-500">
            {needle
              ? 'Geen medewerker met deze naam.'
              : 'Geen actieve medewerkers in deze afdeling.'}
          </p>
        )}
        <ul className="divide-y divide-[#E2E8F0]">
          {employeeList.map((emp) => (
            <li key={emp.id}>
              <button
                type="button"
                data-testid={`medewerker-rij-${emp.id}`}
                onClick={() => setSelectedEmployeeId(emp.id)}
                aria-pressed={emp.id === selectedEmployeeId}
                className={`w-full rounded px-2 py-2 text-left text-sm ${
                  emp.id === selectedEmployeeId
                    ? 'bg-[#2F5A7A] text-white'
                    : 'text-[#1E293B] hover:bg-gray-50'
                }`}
              >
                {emp.name}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex-1">
        {!selectedEmployee && (
          <p
            data-testid="kwalificatie-matrix-geen-medewerker"
            className="py-12 text-center text-sm text-gray-500"
          >
            Kies links een medewerker.
          </p>
        )}

        {selectedEmployee && !hasLists && (
          <p
            data-testid="kwalificatie-matrix-lege-lijsten"
            className="py-12 text-center text-sm text-gray-500"
          >
            Nog geen productcategorieën — beheer de lijst eerst.
          </p>
        )}

        {selectedEmployee && hasLists && (
          // `key` op de medewerker: elke checkbox houdt zijn eigen optimistische
          // staat vast, en geen enkele sleutel eronder bevat het medewerker-id.
          // Zonder deze regel overleeft een net gezet vinkje het klikken op een
          // andere medewerker — die ziet dan een kwalificatie die hij niet heeft.
          <div key={selectedEmployee.id} className="space-y-8">
            {PLANNING_TASK_ACTIVITIES.map((activity) => {
              const sectionMarkets = marketAxis(activity);
              const heeftMarktAs = activityUsesTargetMarket(activity);
              const qualifiedCount = categories.reduce(
                (acc, pc) =>
                  acc +
                  sectionMarkets.filter((tm) =>
                    qualifiedKeys.has(`${activity}:${pc.id}:${targetMarketKey(tm.id)}`)
                  ).length,
                0
              );
              const totalCount = categories.length * sectionMarkets.length;

              return (
                <section key={activity} data-testid={`kwalificatie-sectie-${activity}`}>
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-[#1E293B]">
                      {PLANNING_TASK_ACTIVITY_LABELS[activity]}
                    </h3>
                    <span
                      data-testid={`kwalificatie-sectie-teller-${activity}`}
                      className="text-xs text-gray-500"
                    >
                      {qualifiedCount} van {totalCount} combinaties
                    </span>
                  </div>
                  {heeftMarktAs && sectionMarkets.length === 0 && (
                    <p
                      data-testid={`kwalificatie-sectie-geen-doelmarkten-${activity}`}
                      className="py-4 text-sm text-gray-500"
                    >
                      Deze activiteit werkt per doelmarkt, maar er zijn er nog geen. Beheer de
                      doelmarktenlijst eerst.
                    </p>
                  )}
                  {/* Bij nul doelmarkten hoort onder die melding géén tabel: dat
                      zou een lege raster geven met alleen een categorie-kolom en
                      "0 van 0 combinaties" eronder. */}
                  {/* Eigen scroll-container PER sectie, en géén `w-full`.
                      Beide zijn nodig: met `w-full` rekt een smalle tabel mee
                      tot de breedte van de breedste (vastleggen, 15
                      doelmarkten), waardoor het enige vinkje van een
                      markt-loze activiteit honderden pixels naar rechts belandt
                      — buiten beeld, terwijl het wél in de pagina staat. Een
                      DOM-test vindt zo'n vinkje gewoon; dit is alleen met het
                      oog te zien. */}
                  {sectionMarkets.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="border-collapse text-left text-sm">
                        <thead>
                          <tr>
                            <th
                              scope="col"
                              className="w-64 border-b border-[#E2E8F0] py-1 pr-2 font-medium text-gray-500"
                            >
                              Productcategorie
                            </th>
                            {sectionMarkets.map((tm) => (
                              <th
                                key={targetMarketKey(tm.id)}
                                scope="col"
                                className="border-b border-[#E2E8F0] px-2 py-1 text-center font-medium text-gray-500"
                              >
                                {/* Zonder doelmarkt-as krijgt de kolom geen kop —
                                "Gekwalificeerd" zou suggereren dat de andere
                                kolommen dat niet zijn. */}
                                {tm.name ?? ''}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {categories.map((pc) => (
                            <tr key={pc.id}>
                              <th
                                scope="row"
                                className="border-b border-[#E2E8F0] py-1 pr-2 font-normal text-[#1E293B]"
                              >
                                {pc.name}
                              </th>
                              {sectionMarkets.map((tm) => (
                                <td
                                  key={targetMarketKey(tm.id)}
                                  className="border-b border-[#E2E8F0] px-2 py-1 text-center"
                                >
                                  <QualificationCheckbox
                                    employeeId={selectedEmployee.id}
                                    employeeName={selectedEmployee.name}
                                    activity={activity as PlanningTaskActivity}
                                    productCategoryId={pc.id}
                                    targetMarketId={tm.id}
                                    qualified={qualifiedKeys.has(
                                      `${activity}:${pc.id}:${targetMarketKey(tm.id)}`
                                    )}
                                    ariaLabel={
                                      heeftMarktAs
                                        ? `${selectedEmployee.name}: ${PLANNING_TASK_ACTIVITY_LABELS[activity]}, ${pc.name}, ${tm.name}`
                                        : `${selectedEmployee.name}: ${PLANNING_TASK_ACTIVITY_LABELS[activity]}, ${pc.name}`
                                    }
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
